import { useCallback, useEffect, useRef } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { Core, WebViewerInstance } from "@pdftron/webviewer"
import AnnotationService, { hexToRgb } from "services/annotations.service"
import type { TViewerPane } from "services/annotations.service"
import { useCreateFreehandHighlight } from "hooks/api/highlights/highlights.hook"
import { insertHighlightIntoCache } from "helpers/quotationCache.helper"
import { useFigureHighlightStore } from "store/figureHighlight.store"
import { setToolModeOnAllPanes } from "../core/viewerTools"
import type { AxiosResponse } from "axios"
import type { TCreateFreehandHighlightRequest, TFreehandHighlight } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TViewerMode } from "store/viewer.store"

export type FreehandHighlightCreateContext = {
	instanceRef: MutableRefObject<WebViewerInstance | null>
	// Leaving editor mode (closing the modal back to preview) disarms the tool — preview has no
	// annotate affordances at all, matching every other canvas mode.
	mode: TViewerMode
	// True once WebViewer has finished constructing and `instanceRef.current` is actually populated.
	// Both effects below NEED this in their deps: `instanceRef` is a ref (never a dep change) and the
	// instance is assigned asynchronously inside initWebViewer's `.then`, so an effect that lists only
	// refs runs exactly once — at mount, while the instance is still null — and never again.
	isInitialized: boolean
	// Bumped whenever split screen re-lays-out the viewer (REQ-399). In the listener effect's deps so
	// arming the tool BEFORE split screen is opened still ends up bound on the split pane once it
	// gets a real document — same reasoning as useFigureHighlightCreate's own binding effect.
	layoutEpoch: number
	numericQuotationIdRef: MutableRefObject<number | undefined>
	queryClient: QueryClient
	formatError: (error: unknown) => void
	// Tears down every OTHER canvas mode (connect-objects, the "add a detection" sidebars, the shape
	// tool) before arming. Every canvas-mode entry point in this codebase TAKES OVER rather than
	// silently no-opping, and this must match.
	cancelOtherCanvasModes: () => void
	clearSelectionToolbar: () => void
	// Selects the highlight once persisted, so the trash toolbar shows immediately after drawing —
	// the same "decision 2" behaviour the figure tool has, and what design frame 18916:553467 shows.
	onHighlightCreated?: (highlight: TFreehandHighlight, pane: TViewerPane) => void
}

/**
 * REQ-400 freehand highlight create (#1324).
 *
 * Arms Apryse's native `FreeHandCreateTool` (`ToolNames.FREEHAND`) and, once it commits a
 * `FreeHandAnnotation`, converts the stroke geometry to backend space, POSTs it, then swaps the
 * native draft for the SAME inert render pipeline #1328 built (`AnnotationService.addHighlight`) —
 * so a just-drawn highlight behaves identically to one loaded from persistence.
 *
 * Two things make this genuinely different from the figure tool, not a copy with another class:
 *
 *  1. **Multi-stroke grouping is Apryse's, and we let it happen.** `FreeHandCreateTool.createDelay`
 *     exists to "allow for multiple paths to be added as part of the same annotation", and the
 *     annotation only reaches the AnnotationManager once that delay elapses. So several strokes
 *     drawn in quick succession arrive as ONE annotation carrying several paths — which is exactly
 *     why the backend's `path` is a list OF strokes. Do not call `tool.complete()` to "fix" the
 *     delay: that forces one annotation (and one highlight) per stroke.
 *  2. **There is no fill and no line style.** An Ink annotation has no fill concept, and the
 *     freehand endpoint accepts neither field — hence the smaller options row.
 */
export const useFreehandHighlightCreate = (ctx: FreehandHighlightCreateContext) => {
	const {
		instanceRef,
		mode,
		isInitialized,
		layoutEpoch,
		numericQuotationIdRef,
		queryClient,
		formatError,
		cancelOtherCanvasModes,
		clearSelectionToolbar,
		onHighlightCreated,
	} = ctx

	const isFreehandArmed = useFigureHighlightStore((state) => state.isFreehandArmed)
	const freehandColor = useFigureHighlightStore((state) => state.freehandColor)
	const freehandOpacity = useFigureHighlightStore((state) => state.freehandOpacity)
	const freehandStrokeWidth = useFigureHighlightStore((state) => state.freehandStrokeWidth)
	const armFreehandToolStore = useFigureHighlightStore((state) => state.armFreehandTool)
	const disarmFreehandToolStore = useFigureHighlightStore((state) => state.disarmFreehandTool)

	const { mutateAsync: createFreehandHighlightAsync } = useCreateFreehandHighlight()

	// Read inside the handler, which is bound once per arm and must not go stale when a control
	// changes mid-session (changing one doesn't re-arm the tool).
	const freehandColorRef = useRef(freehandColor)
	freehandColorRef.current = freehandColor
	const freehandOpacityRef = useRef(freehandOpacity)
	freehandOpacityRef.current = freehandOpacity
	const freehandStrokeWidthRef = useRef(freehandStrokeWidth)
	freehandStrokeWidthRef.current = freehandStrokeWidth

	// Every pane's FreeHandCreateTool, skipping a split viewer that has no document yet (the same
	// not-yet-a-real-pane skip resolveViewerPanes makes — getDocumentViewers() reports a second
	// viewer even when split screen has never been opened).
	const forEachFreehandTool = useCallback(
		(run: (tool: Core.Tools.FreeHandCreateTool) => void) => {
			const instance = instanceRef.current
			if (!instance) return
			const { getDocumentViewers, documentViewer, Tools } = instance.Core
			const viewers = getDocumentViewers?.() ?? [documentViewer]
			viewers.forEach((viewer, index) => {
				if (!viewer) return
				if (index > 0 && !viewer.getDocument?.()) return
				const tool = viewer.getTool(Tools.ToolNames.FREEHAND) as unknown as
					| Core.Tools.FreeHandCreateTool
					| undefined
				if (tool) run(tool)
			})
		},
		[instanceRef],
	)

	// Returns whether it actually disarmed anything, so the Escape hotkey chain (onCancelCanvasMode)
	// knows to stop here rather than falling through to "close the right sidebar" — the same contract
	// every other cancelX() in that chain has.
	const disarmFreehandTool = useCallback((): boolean => {
		if (!useFigureHighlightStore.getState().isFreehandArmed) return false
		// Ask the tool to commit a stroke still inside its 1500ms createDelay window, so exiting right
		// after drawing saves that stroke promptly instead of appearing to discard it. This is a
		// PROMPTNESS measure, not a correctness one: `complete()` resolves the add inside a promise
		// chain, so nothing here guarantees it lands before this hook re-renders. Correctness comes
		// from the listener being bound for the whole editor session (see its own note) rather than
		// only while armed. Also a genuine no-op while the pointer is still down — Apryse returns
		// early in that case — which is another reason not to depend on it.
		forEachFreehandTool((tool) => {
			try {
				void tool.complete?.()
			} catch {
				// Best-effort: nothing pending, or a tool instance that doesn't expose complete().
			}
		})
		disarmFreehandToolStore()
		const instance = instanceRef.current
		if (instance) setToolModeOnAllPanes(instance, instance.Core.Tools.ToolNames.EDIT)
		return true
	}, [instanceRef, disarmFreehandToolStore, forEachFreehandTool])

	const armFreehandTool = useCallback(() => {
		const instance = instanceRef.current
		if (!instance) return
		// Always takes over, like every other canvas-mode entry point.
		cancelOtherCanvasModes()
		AnnotationService.deselectAll()
		AnnotationService.deselectHighlight()
		clearSelectionToolbar()
		armFreehandToolStore()
		setToolModeOnAllPanes(instance, instance.Core.Tools.ToolNames.FREEHAND)
	}, [instanceRef, cancelOtherCanvasModes, clearSelectionToolbar, armFreehandToolStore])

	// The toolbar button is a TOGGLE (frame 18916:553416 shows it visibly active while armed). The
	// options row's ✕ is wired to this same handler: that row only exists while armed, so toggling
	// from it can only ever disarm.
	const toggleFreehandTool = useCallback(() => {
		if (useFigureHighlightStore.getState().isFreehandArmed) {
			disarmFreehandTool()
			return
		}
		armFreehandTool()
	}, [armFreehandTool, disarmFreehandTool])

	const handleAnnotationAdded = useCallback(
		async (
			annot: Core.Annotations.Annotation,
			// The manager of the VIEWER whose tool fired this — never assume primary. Split screen
			// runs a second DocumentViewer with its OWN AnnotationManager; force-deleting a draft
			// drawn there through the primary manager is a silent no-op that orphans it forever
			// (the bug #1323's review caught).
			annotationManager: Core.AnnotationManager,
			pane: TViewerPane,
		) => {
			const qid = numericQuotationIdRef.current
			if (qid == null || Number.isNaN(qid)) {
				annotationManager.deleteAnnotations([annot], { force: true })
				return
			}

			// Null when Apryse handed back no usable points — drop the draft rather than POST a
			// degenerate highlight the endpoint would reject.
			const captured = AnnotationService.getBackendPathFromAnnotation(annot)
			if (!captured) {
				annotationManager.deleteAnnotations([annot], { force: true })
				return
			}

			const { pageNumber, path, coordinates } = captured
			const body: TCreateFreehandHighlightRequest = {
				x0: coordinates.x0,
				y0: coordinates.y0,
				x1: coordinates.x1,
				y1: coordinates.y1,
				page_number: pageNumber,
				color: freehandColorRef.current,
				path,
				stroke_width: freehandStrokeWidthRef.current,
				opacity: freehandOpacityRef.current,
			}

			try {
				const { data: created } = await createFreehandHighlightAsync({ quotationId: qid, body })
				// The native draft's job ends here — replace it with the inert pipeline every persisted
				// highlight renders through, rather than leaving Apryse's own movable annotation behind.
				annotationManager.deleteAnnotations([annot], { force: true })
				AnnotationService.addHighlight(created)
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) =>
					insertHighlightIntoCache(cached, created),
				)
				onHighlightCreated?.(created, pane)
			} catch (error) {
				annotationManager.deleteAnnotations([annot], { force: true })
				formatError(error)
			}
		},
		[numericQuotationIdRef, createFreehandHighlightAsync, queryClient, formatError, onHighlightCreated],
	)

	// Detect the native create tool committing a stroke. Bound on the ANNOTATION MANAGER's
	// "annotationChanged", NOT the tool object's own emitter — on this WebViewer 11.9.1 build
	// `getTool(...)` can come back without usable `.on`/`.off`, which silently binds nothing at all
	// (see useFigureHighlightCreate's long note on the same trap).
	//
	// Apryse fires "add" for EVERY addAnnotations call including our own, so the filter has to
	// positively identify a fresh, untracked FreeHandAnnotation:
	//   - `action !== "add"` / `info?.imported` — not a creation, or a programmatic import.
	//   - `instanceof FreeHandAnnotation` — a shape draft or an entity highlight doesn't match.
	//   - `isOwnAnnotation(id)` — true for anything the service already tracks, which crucially
	//     includes addHighlight's own "add" (it registers the id BEFORE calling addAnnotations), so
	//     persisting a highlight can't re-trigger this handler on its own result.
	// NOTE the gate: editor mode + initialized, deliberately NOT `isFreehandArmed`. Binding this to
	// the armed flag looks tighter but is a race: `FreeHandCreateTool.createDelay` is 1500ms on this
	// build, so a stroke can still be in flight when the tool is disarmed, and Apryse hands the
	// annotation to the manager from inside a promise chain (`complete()` resolves the add in a
	// `.then`). Whether that add wins against React's effect cleanup depends on how busy the page is
	// — on a page with many entities it loses, and the stroke is orphaned as a raw, movable,
	// never-persisted annotation. Keeping the listener bound for the whole editor session removes the
	// race outright, and costs nothing: in editor mode our tool is the ONLY producer of an untracked
	// FreeHandAnnotation (Apryse's own signature / freehand tools are unreachable — custom header,
	// disabled element groups, and A/H/S/C swallowed in the capture phase). It also covers the
	// arm-the-other-tool path, where the store flips the flag without going through disarm at all.
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance || mode !== "editor" || !isInitialized) return
		const { Annotations, getDocumentViewers, documentViewer } = instance.Core
		const viewers = getDocumentViewers?.() ?? [documentViewer]

		const unbinds: (() => void)[] = []
		viewers.forEach((viewer, index) => {
			if (!viewer) return
			if (index > 0 && !viewer.getDocument?.()) return
			const pane: TViewerPane = index === 0 ? "primary" : "split"
			const annotationManager = viewer.getAnnotationManager()
			if (!annotationManager) return
			const onChanged = (
				annotations: Core.Annotations.Annotation[],
				action: string,
				info?: { imported?: boolean },
			) => {
				if (action !== "add" || info?.imported) return
				for (const annotation of annotations) {
					if (!(annotation instanceof Annotations.FreeHandAnnotation)) continue
					if (AnnotationService.isOwnAnnotation(annotation.Id)) continue
					void handleAnnotationAdded(annotation, annotationManager, pane)
				}
			}
			annotationManager.addEventListener("annotationChanged", onChanged)
			unbinds.push(() => annotationManager.removeEventListener("annotationChanged", onChanged))
		})

		return () => unbinds.forEach((unbind) => unbind())
	}, [instanceRef, mode, isInitialized, handleAnnotationAdded, layoutEpoch])

	// Keep every pane's freehand tool previewing the chosen color/opacity/width while drawing, so the
	// in-progress stroke already looks like what will be persisted. Alpha is baked into the stroke
	// color (rather than set as `Opacity`) to match exactly how buildHighlightAnnotation renders the
	// persisted highlight. Best-effort: `defaults` is a loosely-typed Apryse surface, and a failure
	// here only costs the live preview — the saved highlight is styled correctly regardless.
	useEffect(() => {
		const instance = instanceRef.current
		if (!instance) return
		try {
			const { Annotations } = instance.Core
			const [r, g, b] = hexToRgb(freehandColor)
			const strokeColor = new Annotations.Color(r, g, b, freehandOpacity / 100)
			forEachFreehandTool((tool) => {
				tool.defaults = {
					StrokeColor: strokeColor,
					// An Ink annotation has no fill, but Apryse's `defaults` type requires the field —
					// a fully transparent color is the honest way to say "none".
					FillColor: new Annotations.Color(0, 0, 0, 0),
					Opacity: 1,
					StrokeThickness: freehandStrokeWidth,
				}
			})
		} catch (error) {
			console.warn("Could not preview the freehand-highlight style on the create tool:", error)
		}
		// `isInitialized` is load-bearing here, not decorative: without it every dep is stable from
		// mount (a ref, a `useCallback([instanceRef])`, and a layoutEpoch that only moves on split
		// screen), so the effect ran once against a null instance and never again — leaving
		// `tool.defaults` unwritten and the in-progress stroke drawn in Apryse's thin opaque default
		// until the POST resolved and re-rendered it. `isFreehandArmed` is listed too so the style is
		// re-asserted on each arm, in case Apryse reset the tool in between.
	}, [
		instanceRef,
		isInitialized,
		isFreehandArmed,
		freehandColor,
		freehandOpacity,
		freehandStrokeWidth,
		layoutEpoch,
		forEachFreehandTool,
	])

	// Preview mode has no annotate affordances — otherwise the tool (and its options row) would still
	// be armed the next time the modal opens in editor mode.
	useEffect(() => {
		if (mode !== "editor") disarmFreehandTool()
	}, [mode, disarmFreehandTool])

	// Unmount safety net. The store is module-level, so it OUTLIVES this hook: an unmount that isn't
	// preceded by a mode flip — the viewer being torn down outright rather than switched to preview —
	// would leave `isFreehandArmed` true and the options row showing on the next open. Mirrors
	// useHighlightSelect's own unmount guard.
	//
	// Resets the store flag ONLY, deliberately not calling disarmFreehandTool(): by unmount the
	// Apryse instance may already be gone, and flushing a stroke or setting a tool mode against a
	// torn-down viewer is exactly the kind of thing that throws during teardown. Empty deps so this
	// runs on unmount, not whenever the callback identity changes.
	useEffect(() => {
		return () => {
			if (useFigureHighlightStore.getState().isFreehandArmed) useFigureHighlightStore.getState().disarmFreehandTool()
		}
	}, [])

	return {
		isFreehandArmed,
		armFreehandTool,
		disarmFreehandTool,
		toggleFreehandTool,
	}
}
